import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { setSession } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { Activity, Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@programmatic-engine.local");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.access_token, data.user);
      toast.success("Welcome back.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 grid-bg">
      <div className="hidden lg:flex lg:col-span-3 items-end p-12 border-r border-border bg-card/60">
        <div className="max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 grid place-items-center bg-primary text-primary-foreground rounded-sm">
              <Activity className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="text-xs txt-mono uppercase tracking-widest text-muted-foreground">internal · v0.1</div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
            Programmatic<br /><span className="text-primary">UGC Forum</span> Engine
          </h1>
          <p className="text-sm text-muted-foreground mt-6 max-w-sm">
            Long-tail SEO forum generation, persona-driven replies, drip publishing
            to WordPress. Built for operators, not marketers.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-4 text-xs txt-mono">
            <div className="border-l border-border pl-3"><div className="text-muted-foreground">VERTICAL</div><div className="mt-1 text-foreground">HOME SERVICES</div></div>
            <div className="border-l border-border pl-3"><div className="text-muted-foreground">MODEL</div><div className="mt-1 text-foreground">GPT-5.4</div></div>
            <div className="border-l border-border pl-3"><div className="text-muted-foreground">PUBLISH</div><div className="mt-1 text-foreground">WP REST</div></div>
          </div>
        </div>
      </div>
      <div className="col-span-1 lg:col-span-2 flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={handleSubmit} data-testid={TESTIDS.loginForm} className="w-full max-w-sm space-y-5">
          <div>
            <div className="text-xs txt-mono uppercase tracking-widest text-muted-foreground mb-1">Sign in</div>
            <h2 className="text-2xl font-semibold tracking-tight">Operator access</h2>
          </div>
          <label className="block">
            <div className="text-xs txt-mono uppercase tracking-wider text-muted-foreground mb-1">Email</div>
            <input
              data-testid={TESTIDS.loginEmail}
              type="text" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none px-3 py-2 text-sm rounded-sm txt-mono"
            />
          </label>
          <label className="block">
            <div className="text-xs txt-mono uppercase tracking-wider text-muted-foreground mb-1">Password</div>
            <input
              data-testid={TESTIDS.loginPassword}
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none px-3 py-2 text-sm rounded-sm txt-mono"
            />
          </label>
          <button
            data-testid={TESTIDS.loginSubmit}
            type="submit" disabled={busy}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-3 py-2 text-sm font-medium rounded-sm transition-colors flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Signing in..." : "Sign in →"}
          </button>
          <p className="text-[11px] text-muted-foreground mt-6 leading-relaxed">
            Internal tool. Access is restricted to authorized operators only.
          </p>
        </form>
      </div>
    </div>
  );
}
